import * as React from "react";
import { Link } from "react-router-dom";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";
// components
import TopInterestItem from "../interestItem/topInterestItem";
import DraggableList from "common/components/draggableList";
import StarHandler from "common/components/handlers/starHandler";
import MoveHandler, {
  MOVE_CLASS,
} from "common/components/handlers/moveHandler";
import BoostHandler from "common/components/handlers/boostHandler";
import NoData from "../noData";
// styles

import { VingleURL } from "@vingle/wtf-sdk/url";
const styles = require("./topInterestsList.scss");

interface IProps {
  isLoading: boolean;
  interests: ReadonlyArray<Vingle.UserProfile.IProfileInterestState>;
  listTitle: string;
  username: string;
  isMine: boolean;
  canEdit: boolean;
  onChangeOrder: (oldIndex: number, newIndex: number) => void;
  onBoostClick: (interest: Vingle.UserProfile.IProfileInterestState) => void;
  onMainInterestClick: (
    interest: Vingle.UserProfile.IProfileInterestState,
  ) => void;
}

class TopInterestsList extends React.PureComponent<
  IProps & WrappedComponentProps
> {
  public render() {
    const {
      intl,
      interests,
      listTitle,
      isLoading,
      isMine,
      username,
      canEdit,
      onChangeOrder,
    } = this.props;

    const items = interests.map(
      (interest: Vingle.UserProfile.IProfileInterestState) => {
        if (interest.data) {
          let handlers: React.ReactNode;
          if (isMine && canEdit) {
            handlers = (
              <div className={styles.handlerWrapper}>
                <StarHandler
                  data-interest={interest.data.interest.name}
                  onClick={this.handleStarClick}
                  checked={true}
                />
                <MoveHandler />
              </div>
            );
          } else {
            handlers = (
              <div className={styles.handlerWrapper}>
                <BoostHandler
                  data-interest={interest.data.interest.name}
                  onClick={this.handleBoostClick}
                  isBoosted={interest.data.relation.boost}
                />
              </div>
            );
          }

          return (
            <TopInterestItem
              key={`top_interest_item_${interest.data.interest.name}`}
              interest={interest.data}
              username={username}
            >
              {handlers}
            </TopInterestItem>
          );
        }
      },
    );

    let addMoreTopInterestGuide: React.ReactNode | null = null;
    if (isMine && !canEdit) {
      const userInterestEditURL = new VingleURL.UserShowEditInterests({
        username,
      }).toString();
      if (interests.length < 3) {
        addMoreTopInterestGuide = (
          <Link to={userInterestEditURL}>
            <div className={styles.addTop}>
              <FormattedMessage id="interests.add_top_interest.more" />
            </div>
          </Link>
        );
      } else if (interests.length === 3) {
        addMoreTopInterestGuide = (
          <Link to={userInterestEditURL}>
            <div className={styles.addTop}>
              <FormattedMessage id="interests.add_top_interest.one" />
            </div>
          </Link>
        );
      }
    }

    let visibleContent: React.ReactNode;
    if (isMine) {
      visibleContent = <div>{addMoreTopInterestGuide}</div>;
    } else if (items.length === 0) {
      visibleContent = (
        <NoData
          message={intl.formatMessage({
            id: "interests.no_top_interests_data",
          })}
        />
      );
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.title}>{listTitle}</div>
        <DraggableList
          onChangeOrder={onChangeOrder}
          disabled={isLoading}
          handleClass={MOVE_CLASS}
        >
          {items}
        </DraggableList>
        {visibleContent}
      </div>
    );
  }

  private readonly handleStarClick: React.MouseEventHandler<
    HTMLDivElement
  > = e => {
    const { onMainInterestClick } = this.props;
    const interest = this.findInterest(
      e.currentTarget.getAttribute("data-interest"),
    );
    if (onMainInterestClick && interest) {
      onMainInterestClick(interest);
    }
  };

  private readonly handleBoostClick: React.MouseEventHandler<
    HTMLDivElement
  > = e => {
    const { onBoostClick } = this.props;
    const interest = this.findInterest(
      e.currentTarget.getAttribute("data-interest"),
    );
    if (onBoostClick && interest) {
      onBoostClick(interest);
    }
  };

  private readonly findInterest = (
    interestName: string | null,
  ): Vingle.UserProfile.IProfileInterestState | undefined => {
    const { interests } = this.props;
    return interests.filter(interest => {
      return interest.data && interest.data.interest.name === interestName;
    })[0];
  };
}

export default injectIntl(TopInterestsList);
